import { supabase } from '@/lib/supabaseClient';
import type {
  Dashboard,
  DashboardWidget,
  DashboardWithWidgets,
  DashboardPermission,
  CreateDashboardInput,
  UpdateDashboardInput,
  AddWidgetInput,
  UpdateWidgetInput,
  UpdateWidgetPositionInput,
  WidgetTemplate,
  CompatibilityIssue,
  TemplateMappingResult,
  MappingSummary,
  WidgetPosition,
} from '@/types/dashboard';
import { getCurrentTenantId } from '@/utils/tenantUtils';
import { WidgetMappingService } from './widgetMappingService';
import { logDashboardActivity } from '@/utils/activityLogger';
import {
  DASHBOARD_ACCOUNT_ADMIN_PERMISSION,
  DASHBOARD_PLATFORM_PERMISSION,
  adminMayEditDashboard,
} from '@/lib/dashboardAccess';

export class DashboardService {
  /**
   * Fetches all dashboards for the current tenant
   * Explicitly filters by tenant_id to respect tenant switcher context (even for Superadmins)
   */
  static async getDashboards(): Promise<Dashboard[]> {
    try {
      const tenantId = await getCurrentTenantId();

      const { data, error } = await supabase
        .from('dashboards')
        .select('*')
        .eq('tenant_id', tenantId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Failed to fetch dashboards:', error);
      throw new Error('Unable to load dashboards. Please try again.');
    }
  }

  /**
   * Verifies if user can access a dashboard via RLS policies
   * DASH-22.3: Used to validate dashboard accessibility before redirecting
   *
   * @param dashboardId - Dashboard ID to check
   * @returns true if accessible, false if denied/deleted
   */
  static async verifyDashboardAccess(dashboardId: string): Promise<boolean> {
    try {
      // Attempt to fetch dashboard - RLS will deny if user doesn't have access
      const { data, error } = await supabase
        .from('dashboards')
        .select('id')
        .eq('id', dashboardId)
        .maybeSingle();

      // If error or no data, dashboard is not accessible
      if (error || !data) {
        console.warn(`[verifyDashboardAccess] Dashboard ${dashboardId} not accessible:`, error?.message);
        return false;
      }

      return true;
    } catch (error) {
      console.error('[verifyDashboardAccess] Error checking access:', error);
      return false;
    }
  }

  /**
   * Fetches the default dashboard using 3-tier priority resolution
   * DASH-22.3: Integration of user, role, and tenant defaults
   *
   * Priority chain (early exit on first valid match):
   * 1. User Default (DASH-22.1) - Personal preference
   * 2. Role Default (DASH-22.2) - Role-based assignment
   * 3. Tenant Default (existing) - Organization-wide default
   * 4. No Default - Returns null
   *
   * Each tier verifies dashboard accessibility via RLS before returning.
   *
   * @returns Default dashboard or null if none found
   */
  static async getDefaultDashboard(): Promise<Dashboard | null> {
    try {
      const tenantId = await getCurrentTenantId();

      // Priority 1: User Default (DASH-22.1)
      console.log('[DefaultDashboard] Checking user default...');
      const userDefault = await this.getUserDefaultDashboard();
      if (userDefault) {
        console.log('[DefaultDashboard] User default:', userDefault.name);
        // Verify accessibility via RLS
        const isAccessible = await this.verifyDashboardAccess(userDefault.id);
        if (isAccessible) return userDefault;
        // If not accessible, clear user preference and continue
        console.warn('[DefaultDashboard] User default not accessible, clearing preference');
        await this.clearUserDefaultDashboard();
      }

      // Priority 2: Role Default (DASH-22.2)
      console.log('[DefaultDashboard] Checking role default...');
      const roleDefault = await this.getRoleDefaultDashboard();
      if (roleDefault) {
        console.log('[DefaultDashboard] Role default:', roleDefault.name);
        const isAccessible = await this.verifyDashboardAccess(roleDefault.id);
        if (isAccessible) return roleDefault;
        // Role default not accessible, continue to tenant default
        console.warn('[DefaultDashboard] Role default not accessible, trying tenant default');
      }

      // Priority 3: Tenant Default (existing)
      console.log('[DefaultDashboard] Checking tenant default...');
      const { data: tenantDefault, error: tenantError } = await supabase
        .from('dashboards')
        .select('*')
        .eq('is_default', true)
        .eq('tenant_id', tenantId)
        .maybeSingle();

      if (tenantError) throw tenantError;
      if (tenantDefault) {
        console.log('[DefaultDashboard] Tenant default:', tenantDefault.name);
        const isAccessible = await this.verifyDashboardAccess(tenantDefault.id);
        if (isAccessible) return tenantDefault;
        console.warn('[DefaultDashboard] Tenant default not accessible');
      }

      // Priority 4: No default found
      console.log('[DefaultDashboard] No default found');
      return null;
    } catch (error) {
      console.error('Failed to fetch default dashboard:', error);
      return null;
    }
  }

  /**
   * Fetches the user's personal default dashboard preference
   * DASH-22.1: User-level default dashboard (Priority 1 in 3-tier system)
   * @returns User's default dashboard or null if not set
   */
  static async getUserDefaultDashboard(): Promise<Dashboard | null> {
    try {
      const tenantId = await getCurrentTenantId();
      const { data: userData } = await supabase.auth.getUser();
      const userId = userData?.user?.id;

      if (!userId) return null;

      // Query user_preferences for current user + tenant
      const { data: preference, error: prefError } = await supabase
        .from('user_preferences')
        .select('default_dashboard_id')
        .eq('user_id', userId)
        .eq('tenant_id', tenantId)
        .maybeSingle();

      if (prefError) throw prefError;
      if (!preference || !preference.default_dashboard_id) return null;

      // Fetch the dashboard by default_dashboard_id
      const { data: dashboard, error: dashboardError } = await supabase
        .from('dashboards')
        .select('*')
        .eq('id', preference.default_dashboard_id)
        .maybeSingle();

      if (dashboardError) throw dashboardError;
      return dashboard;
    } catch (error) {
      console.error('Failed to fetch user default dashboard:', error);
      return null;
    }
  }

  /**
   * Sets a dashboard as the user's personal default
   * DASH-22.1: User-level default dashboard preference
   * @param dashboardId - Dashboard ID to set as default
   */
  static async setUserDefaultDashboard(dashboardId: string): Promise<void> {
    try {
      const tenantId = await getCurrentTenantId();
      const { data: userData } = await supabase.auth.getUser();
      const userId = userData?.user?.id;

      if (!userId) {
        throw new Error('User not authenticated');
      }

      // Upsert user_preferences record (INSERT ON CONFLICT UPDATE)
      const { error } = await supabase
        .from('user_preferences')
        .upsert({
          user_id: userId,
          tenant_id: tenantId,
          default_dashboard_id: dashboardId,
          updated_at: new Date().toISOString(),
        }, {
          onConflict: 'user_id,tenant_id',
        });

      if (error) throw error;
    } catch (error) {
      console.error('Failed to set user default dashboard:', error);
      throw new Error('Unable to set default dashboard. Please try again.');
    }
  }

  /**
   * Clears the user's personal default dashboard preference
   * DASH-22.1: Remove user-level default dashboard
   */
  static async clearUserDefaultDashboard(): Promise<void> {
    try {
      const tenantId = await getCurrentTenantId();
      const { data: userData } = await supabase.auth.getUser();
      const userId = userData?.user?.id;

      if (!userId) return;

      // Update user_preferences to clear default_dashboard_id
      const { error } = await supabase
        .from('user_preferences')
        .update({
          default_dashboard_id: null,
          updated_at: new Date().toISOString(),
        })
        .eq('user_id', userId)
        .eq('tenant_id', tenantId);

      if (error) throw error;
    } catch (error) {
      console.error('Failed to clear user default dashboard:', error);
      throw new Error('Unable to clear default dashboard. Please try again.');
    }
  }

  /**
   * Fetches the role-based default dashboard for the current user
   * DASH-22.2: Role-level default dashboard (Priority 2 in 3-tier system)
   *
   * Finds dashboards assigned as defaults for the user's role(s) in current tenant.
   * Uses PostgreSQL array overlap operator to match user's role IDs with dashboard's default_for_role_ids.
   *
   * @returns Role default dashboard or null if not found
   */
  static async getRoleDefaultDashboard(): Promise<Dashboard | null> {
    try {
      const tenantId = await getCurrentTenantId();
      const { data: userData } = await supabase.auth.getUser();
      const userId = userData?.user?.id;

      if (!userId) return null;

      // Step 1: Collect user's role IDs using legacy-first pattern.
      // Normal users hold their role on users.role_id; superadmins have multi-tenant
      // assignments in user_roles. Union both so role-default dashboards apply to all.
      const roleIdSet = new Set<number>();

      const { data: userRow, error: userRowError } = await supabase
        .from('users')
        .select('role_id')
        .eq('id', userId)
        .maybeSingle();

      if (userRowError) throw userRowError;
      if (userRow?.role_id) roleIdSet.add(userRow.role_id);

      const { data: userRoles, error: roleError } = await supabase
        .from('user_roles')
        .select('role_id')
        .eq('user_id', userId)
        .eq('tenant_id', tenantId);

      if (roleError) throw roleError;
      userRoles?.forEach(ur => {
        if (ur.role_id != null) roleIdSet.add(ur.role_id);
      });

      if (roleIdSet.size === 0) return null;

      // Step 2: Extract role IDs into array
      const roleIds = Array.from(roleIdSet);

      // Step 3: Find dashboard where user's role matches default_for_role_ids
      // Uses .overlaps() operator for PostgreSQL array overlap check (&&)
      const { data: dashboard, error: dashboardError } = await supabase
        .from('dashboards')
        .select('*')
        .eq('tenant_id', tenantId)
        .eq('is_role_default', true)
        .overlaps('default_for_role_ids', roleIds)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (dashboardError) throw dashboardError;
      return dashboard;
    } catch (error) {
      console.error('Failed to fetch role default dashboard:', error);
      return null;
    }
  }

  /**
   * Fetches all global roles available for role default assignment
   * DASH-22.2: Used by DashboardSettingsDrawer for role selection
   *
   * Note: All 18 roles in the system are global (global=true), meaning they're available
   * to all tenants. However, role assignments (user_roles) and role default dashboards
   * are tenant-specific.
   *
   * @returns Array of Role objects with id, name, description
   */
  static async getTenantRoles(): Promise<Array<{ id: number; name: string; description: string | null }>> {
    try {
      const { data, error } = await supabase
        .from('roles')
        .select('id, name, description')
        .eq('global', true)
        .order('name', { ascending: true });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Failed to fetch tenant roles:', error);
      throw new Error('Unable to load roles. Please try again.');
    }
  }

  /**
   * Fetches a single dashboard with its widgets
   */
  static async getDashboard(id: string): Promise<DashboardWithWidgets | null> {
    try {
      const { data, error } = await supabase
        .from('dashboards')
        .select(`
          *,
          widgets:dashboard_widgets(
            id,
            dashboard_id,
            widget_type_id,
            position,
            config,
            is_dynamic,
            dynamic_config,
            created_at,
            updated_at
          )
        `)
        .eq('id', id)
        .single();

      if (error) throw error;
      return data as DashboardWithWidgets;
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
      throw new Error('Unable to load dashboard. Please try again.');
    }
  }

  /**
   * Creates a new dashboard
   */
  static async createDashboard(input: CreateDashboardInput): Promise<Dashboard> {
    try {
      const tenantId = await getCurrentTenantId();
      const { data: userData } = await supabase.auth.getUser();

      const { data, error } = await supabase
        .from('dashboards')
        .insert([{
          tenant_id: tenantId,
          name: input.name,
          description: input.description || null,
          created_by: userData?.user?.id || '',
          is_default: false,
          layout_config: {
            cols: 12,
            rowHeight: 60,
          },
        }])
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Failed to create dashboard:', error);
      throw new Error('Unable to create dashboard. Please try again.');
    }
  }

  /**
   * Updates a dashboard
   */
  static async updateDashboard(id: string, input: UpdateDashboardInput): Promise<Dashboard> {
    try {
      const { data, error } = await supabase
        .from('dashboards')
        .update({
          name: input.name,
          description: input.description,
          is_default: input.is_default,
          layout_config: input.layout_config,
          // DASH-22.2: Role-based default dashboard assignment
          is_role_default: input.is_role_default,
          default_for_role_ids: input.default_for_role_ids,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Failed to update dashboard:', error);
      throw new Error('Unable to update dashboard. Please try again.');
    }
  }

  /**
   * Deletes a dashboard
   */
  static async deleteDashboard(id: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('dashboards')
        .delete()
        .eq('id', id);

      if (error) throw error;
    } catch (error) {
      console.error('Failed to delete dashboard:', error);
      throw new Error('Unable to delete dashboard. Please try again.');
    }
  }

  /**
   * Validates a widget position before saving
   * DASH-21.1: Prevent invalid positions from being persisted
   */
  private static validatePosition(position: WidgetPosition): boolean {
    const { x, y, w, h } = position;

    // Check for invalid values
    if (x < 0 || y < 0) {
      console.error('[DashboardService] Invalid position: negative coordinates', { x, y });
      return false;
    }

    if (w < 1 || h < 1) {
      console.error('[DashboardService] Invalid position: dimensions too small', { w, h });
      return false;
    }

    // Check grid bounds (12 columns for lg breakpoint)
    if (x + w > 12) {
      console.error('[DashboardService] Invalid position: extends beyond grid', { x, w, total: x + w });
      return false;
    }

    return true;
  }

  /**
   * Updates widget positions for a dashboard (bulk update)
   * DASH-21.1: Added position validation before save
   */
  static async updateDashboardLayout(
    widgetUpdates: UpdateWidgetPositionInput[]
  ): Promise<void> {
    try {
      // DASH-21.1: Validate all positions before saving
      for (const { widgetId, position } of widgetUpdates) {
        if (!this.validatePosition(position)) {
          throw new Error(`Invalid position for widget ${widgetId}. Please adjust and try again.`);
        }
      }

      // Bulk update - loop through and update each widget
      for (const { widgetId, position } of widgetUpdates) {
        const { error } = await supabase
          .from('dashboard_widgets')
          .update({
            position,
            updated_at: new Date().toISOString(),
          })
          .eq('id', widgetId);

        if (error) throw error;
      }
    } catch (error) {
      console.error('Failed to update layout:', error);
      throw new Error('Unable to save layout. Please try again.');
    }
  }

  /**
   * Fetches a single widget by ID with its library reference
   */
  static async getWidget(widgetId: string): Promise<DashboardWidget | null> {
    try {
      const { data, error } = await supabase
        .from('dashboard_widgets')
        .select(`
          id,
          dashboard_id,
          widget_type_id,
          position,
          config,
          is_dynamic,
          dynamic_config,
          created_at,
          updated_at,
          widget_library(
            id,
            name,
            description,
            icon,
            category,
            default_config,
            dynamic_config,
            min_width,
            min_height,
            is_system,
            is_global,
            tenant_id
          )
        `)
        .eq('id', widgetId)
        .single();

      if (error) throw error;

      // Transform widget_library from array to object (Supabase returns array for joined data)
      if (data) {
        if (Array.isArray(data.widget_library) && data.widget_library.length > 0) {
          const library = data.widget_library[0];
          return {
            ...data,
            widget_library: {
              name: library.name,
              description: library.description,
              icon: library.icon || null,
              is_system: library.is_system,
            },
          } as DashboardWidget;
        } else {
          // If no widget_library or empty array, return without that field
          const { widget_library, ...rest } = data;
          return rest as DashboardWidget;
        }
      }

      return null;
    } catch (error) {
      console.error('Failed to fetch widget:', error);
      return null;
    }
  }

  /**
   * Adds a widget to a dashboard
   */
  static async addWidget(dashboardId: string, input: AddWidgetInput): Promise<DashboardWidget> {
    try {
      const { data, error } = await supabase
        .from('dashboard_widgets')
        .insert([{
          dashboard_id: dashboardId,
          widget_type_id: input.widget_type_id,
          position: input.position,
          config: input.config || null,
          is_dynamic: input.is_dynamic || false, // DASH-8.5: Support dynamic widgets
        }])
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Failed to add widget:', error);
      throw new Error('Unable to add widget. Please try again.');
    }
  }

  /**
   * Duplicates a widget on a dashboard
   * Creates a copy with the same config but offset position
   */
  static async duplicateWidget(
    widgetId: string,
    dashboardId: string
  ): Promise<DashboardWidget> {
    try {
      // 1. Fetch the original widget
      const { data: originalWidget, error: fetchError } = await supabase
        .from('dashboard_widgets')
        .select('*')
        .eq('id', widgetId)
        .single();

      if (fetchError) throw fetchError;
      if (!originalWidget) throw new Error('Widget not found');

      // 2. Calculate offset position
      let newPosition = {
        x: originalWidget.position.x + 1,
        y: originalWidget.position.y,
        w: originalWidget.position.w,
        h: originalWidget.position.h,
      };

      // 3. Check boundary (12 columns max)
      if (newPosition.x + newPosition.w > 12) {
        // Wrap to new row
        newPosition = {
          x: 0,
          y: originalWidget.position.y + originalWidget.position.h,
          w: originalWidget.position.w,
          h: originalWidget.position.h,
        };
      }

      // 4. Create duplicate widget
      const { data: newWidget, error: insertError } = await supabase
        .from('dashboard_widgets')
        .insert([{
          dashboard_id: dashboardId,
          widget_type_id: originalWidget.widget_type_id,
          position: newPosition,
          config: originalWidget.config,
          is_dynamic: originalWidget.is_dynamic,
          dynamic_config: originalWidget.dynamic_config,
        }])
        .select()
        .single();

      if (insertError) throw insertError;
      return newWidget;
    } catch (error) {
      console.error('Failed to duplicate widget:', error);
      throw new Error('Unable to duplicate widget. Please try again.');
    }
  }

  /**
   * Updates a widget
   */
  static async updateWidget(widgetId: string, input: UpdateWidgetInput): Promise<DashboardWidget> {
    try {
      const { data, error } = await supabase
        .from('dashboard_widgets')
        .update({
          position: input.position,
          config: input.config,
          updated_at: new Date().toISOString(),
        })
        .eq('id', widgetId)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Failed to update widget:', error);
      throw new Error('Unable to update widget. Please try again.');
    }
  }

  /**
   * Updates widget configuration only (Story DASH-3.1)
   */
  static async updateWidgetConfig(
    widgetId: string,
    config: Record<string, any>
  ): Promise<DashboardWidget> {
    try {
      const { data, error } = await supabase
        .from('dashboard_widgets')
        .update({
          config,
          updated_at: new Date().toISOString(),
        })
        .eq('id', widgetId)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Failed to update widget config:', error);
      throw new Error('Unable to update widget configuration. Please try again.');
    }
  }

  /**
   * Deletes a widget
   */
  static async deleteWidget(widgetId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('dashboard_widgets')
        .delete()
        .eq('id', widgetId);

      if (error) throw error;
    } catch (error) {
      console.error('Failed to delete widget:', error);
      throw new Error('Unable to delete widget. Please try again.');
    }
  }

  /**
   * Fetches widget templates (all available widget types)
   */
  static async getWidgetTemplates(): Promise<WidgetTemplate[]> {
    try {
      const { data, error} = await supabase
        .from('widget_templates')
        .select('*')
        .order('category', { ascending: true })
        .order('name', { ascending: true });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Failed to fetch widget templates:', error);
      throw new Error('Unable to load widget templates. Please try again.');
    }
  }

  /**
   * Saves a widget configuration as a reusable template (DASH-17.2)
   * Creates a tenant-specific template that can be used across dashboards
   */
  static async saveWidgetAsTemplate(input: {
    name: string;
    description?: string;
    category?: string; // Optional: defaults to null
    config: Record<string, any>;
    icon?: string;
    minWidth?: number;
    minHeight?: number;
  }): Promise<WidgetTemplate> {
    try {
      const tenantId = await getCurrentTenantId();

      // Generate unique template ID (kebab-case-name-timestamp)
      const kebabName = input.name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
      const templateId = `${kebabName}-${Date.now()}`;

      const { data, error } = await supabase
        .from('widget_library')
        .insert([{
          id: templateId,
          name: input.name,
          description: input.description || null,
          category: input.category || null, // Allow null categories
          is_system: false,
          is_global: false,
          tenant_id: tenantId,
          dynamic_config: input.config,
          icon: input.icon || 'sparkles',
          min_width: input.minWidth || 4,
          min_height: input.minHeight || 3,
        }])
        .select()
        .single();

      if (error) throw error;

      // DASH-23.2: Log template creation (fire-and-forget)
      if (tenantId) {
        logDashboardActivity.templateSaved(
          templateId,
          tenantId,
          {
            template_name: input.name,
            category: input.category,
          }
        );
      }

      return data as WidgetTemplate;
    } catch (error) {
      console.error('Failed to save widget as template:', error);
      throw new Error('Unable to save template. Please try again.');
    }
  }

  /**
   * Updates template metadata (DASH-17.3)
   * For system templates: Creates a tenant-specific copy
   * For tenant templates: Updates existing template
   */
  static async updateTemplate(
    id: string,
    updates: {
      name?: string;
      description?: string;
      category?: string;
    }
  ): Promise<WidgetTemplate> {
    try {
      const tenantId = await getCurrentTenantId();

      // First, check if this is a system template
      const { data: existingTemplate, error: fetchError } = await supabase
        .from('widget_library')
        .select('*')
        .eq('id', id)
        .single();

      if (fetchError) throw fetchError;

      // If it's a system template, create a tenant-specific copy
      if (existingTemplate.is_system) {
        // Generate new ID for tenant copy
        const kebabName = (updates.name || existingTemplate.name)
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-|-$/g, '');
        const newTemplateId = `${kebabName}-${Date.now()}`;

        const { data: newTemplate, error: createError } = await supabase
          .from('widget_library')
          .insert([{
            id: newTemplateId,
            name: updates.name || existingTemplate.name,
            description: updates.description !== undefined ? updates.description : existingTemplate.description,
            category: updates.category || existingTemplate.category,
            is_system: false,
            is_global: false,
            tenant_id: tenantId,
            dynamic_config: existingTemplate.dynamic_config || existingTemplate.default_config, // Fallback to default_config for old templates
            icon: existingTemplate.icon,
            min_width: existingTemplate.min_width,
            min_height: existingTemplate.min_height,
          }])
          .select()
          .single();

        if (createError) throw createError;
        return newTemplate as WidgetTemplate;
      }

      // If it's already a tenant template, update it
      const { data, error } = await supabase
        .from('widget_library')
        .update({
          name: updates.name,
          description: updates.description,
          category: updates.category,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id)
        .eq('is_system', false)      // Only tenant templates
        .eq('tenant_id', tenantId)   // Only current tenant
        .select()
        .single();

      if (error) throw error;
      return data as WidgetTemplate;
    } catch (error) {
      console.error('Failed to update template:', error);
      throw new Error('Unable to update template. Please try again.');
    }
  }

  /**
   * Deletes a template from the widget library (DASH-17.3)
   * Regular users: Can only delete their tenant's non-system templates
   * Superadmins: Can delete ANY template (including system templates and other tenant templates)
   * Does NOT affect existing widget instances on dashboards
   */
  static async deleteTemplate(id: string): Promise<void> {
    try {
      // Check if current user is Superadmin
      const { data: userData } = await supabase.auth.getUser();
      const userId = userData?.user?.id;

      if (!userId) {
        throw new Error('User not authenticated');
      }

      // Check if user has Superadmin role (simplified check using role name)
      const tenantId = await getCurrentTenantId();
      const { data: superadminCheck } = await supabase
        .from('user_roles')
        .select('role_id, roles!inner(id, name)')
        .eq('user_id', userId)
        .eq('tenant_id', tenantId);

      const isSuperadmin = superadminCheck?.some((ur: any) =>
        ur.roles?.name === 'Superadmin'
      );

      let query = supabase
        .from('widget_library')
        .delete()
        .eq('id', id);

      // If NOT Superadmin, add restrictions
      if (!isSuperadmin) {
        query = query
          .eq('is_system', false)      // Only tenant templates
          .eq('tenant_id', tenantId);  // Only current tenant
      }
      // If Superadmin, no restrictions (can delete any template)

      const { error } = await query;

      if (error) throw error;
    } catch (error) {
      console.error('Failed to delete template:', error);
      throw new Error('Unable to delete template. Please try again.');
    }
  }

  /**
   * Shares a widget template with multiple destination tenants (DASH-22.0)
   * Creates copies of the template with ID mapping to prevent broken widgets
   *
   * @param templateId - ID of the template to share
   * @param destinationTenantIds - Array of destination tenant IDs
   * @returns Object with success status, message, and mapping results
   */
  static async shareTemplateWithTenants(
    templateId: string,
    destinationTenantIds: string[]
  ): Promise<{
    success: boolean;
    message: string;
    mappingResults?: TemplateMappingResult[];
  }> {
    try {
      // 1. Fetch source template with tenant verification
      const { data: sourceTemplate, error: fetchError } = await supabase
        .from('widget_library')
        .select('*, tenants!inner(company_name)')
        .eq('id', templateId)
        .single();

      if (fetchError || !sourceTemplate) {
        throw new Error('Template not found');
      }

      const sourceTenantId = sourceTemplate.tenant_id;
      const sourceTenantName = (sourceTemplate.tenants as any)?.company_name || 'Unknown';

      const mappingResults: TemplateMappingResult[] = [];

      // 2. Share to each target tenant with ID mapping
      for (const targetTenantId of destinationTenantIds) {
        try {
          // 3. Map template config IDs from source to target tenant
          const mappedConfig = await this.mapTemplateConfig(
            sourceTemplate.dynamic_config,
            sourceTenantId,
            targetTenantId
          );

          // 4. Create mapping summary
          const mappingSummary = this.generateMappingSummary(
            sourceTemplate.dynamic_config,
            mappedConfig,
            sourceTenantName
          );

          // 5. Generate unique template ID
          const timestamp = Date.now();
          const tenantPrefix = targetTenantId.slice(0, 8);
          const newTemplateId = `${templateId.split('-')[0]}-shared-${timestamp}-${tenantPrefix}`;

          // 6. Insert template with mapped config
          const { error: insertError } = await supabase
            .from('widget_library')
            .insert({
              id: newTemplateId,
              name: sourceTemplate.name,
              description: mappingSummary.description,
              category: sourceTemplate.category,
              is_system: false,
              is_global: false,
              tenant_id: targetTenantId,
              dynamic_config: mappedConfig, // ✅ MAPPED CONFIG!
              icon: sourceTemplate.icon,
              min_width: sourceTemplate.min_width,
              min_height: sourceTemplate.min_height,
            });

          if (insertError) throw insertError;

          mappingResults.push({
            targetTenantId,
            success: true,
            filtersOriginal: mappingSummary.originalCount,
            filtersMapped: mappingSummary.mappedCount,
            filtersRemoved: mappingSummary.removedCount,
            message: mappingSummary.userMessage,
          });
        } catch (err) {
          console.error(`Failed to share template to tenant ${targetTenantId}:`, err);
          mappingResults.push({
            targetTenantId,
            success: false,
            message: err instanceof Error ? err.message : 'Unknown error',
          });
        }
      }

      // 7. Generate user-friendly success message
      const successCount = mappingResults.filter((r) => r.success).length;
      const failureCount = mappingResults.length - successCount;

      let message = `Template shared to ${successCount} tenant(s)`;
      if (failureCount > 0) {
        message += `, ${failureCount} failed`;
      }

      // Add mapping warnings if any filters were removed
      const totalRemoved = mappingResults.reduce((sum, r) => sum + (r.filtersRemoved || 0), 0);
      if (totalRemoved > 0) {
        message += `. Warning: ${totalRemoved} filter(s) could not be mapped and were removed.`;
      }

      // DASH-23.2: Log template sharing (fire-and-forget)
      if (successCount > 0) {
        // Get target tenant names for logging
        const { data: targetTenants } = await supabase
          .from('tenants')
          .select('tenant_id, company_name')
          .in('tenant_id', destinationTenantIds);

        const targetTenantNames = targetTenants?.map(t => t.company_name) || [];

        logDashboardActivity.templateShared(
          templateId,
          sourceTenantId,
          {
            template_name: sourceTemplate.name,
            target_tenant_ids: destinationTenantIds,
            target_tenant_names: targetTenantNames,
            mapping_summary: message,
          }
        );
      }

      return {
        success: successCount > 0,
        message,
        mappingResults,
      };
    } catch (error) {
      console.error('Error sharing template:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Failed to share template',
      };
    }
  }

  /**
   * Map template config IDs from source to target tenant (DASH-22.0)
   * Returns new config with mapped IDs (unmapped IDs removed)
   *
   * @param sourceConfig - Original template dynamic_config
   * @param sourceTenantId - Source tenant UUID
   * @param targetTenantId - Target tenant UUID
   * @returns Mapped config with target tenant IDs
   */
  private static async mapTemplateConfig(
    sourceConfig: any,
    sourceTenantId: string,
    targetTenantId: string
  ): Promise<any> {
    // Clone config to avoid mutating source
    const mappedConfig = JSON.parse(JSON.stringify(sourceConfig));

    // Extract all tenant-specific IDs from config
    const filters = mappedConfig.filters || {};

    const sourcePipelineIds = filters.pipelineIds || [];
    const sourceStageIds = filters.hiddenStageIds || [];

    // Extract checklist item IDs from projectFields
    const sourceChecklistItemIds: number[] = [];
    (filters.projectFields || []).forEach((field: any) => {
      if (field.source === 'checklist_item' && field.checklistItemId) {
        sourceChecklistItemIds.push(Number(field.checklistItemId));
      }
    });

    // 1. Map Pipeline IDs
    const pipelineMapping = await WidgetMappingService.mapPipelineIds(
      sourcePipelineIds,
      sourceTenantId,
      targetTenantId
    );

    // Replace with mapped IDs (only successfully mapped ones)
    mappedConfig.filters.pipelineIds = Object.values(pipelineMapping.mapped);

    // 2. Map Stage IDs (requires pipeline mapping context and source tenant)
    const stageMapping = await WidgetMappingService.mapStageIds(
      sourceStageIds,
      pipelineMapping.mapped,
      sourceTenantId,
      targetTenantId
    );

    // Replace with mapped IDs
    mappedConfig.filters.hiddenStageIds = Object.values(stageMapping.mapped);

    // 3. Map Checklist Item IDs
    const checklistMapping = await WidgetMappingService.mapChecklistItemIds(
      sourceChecklistItemIds,
      sourceTenantId,
      targetTenantId
    );

    // Update projectFields with mapped checklist item IDs
    if (mappedConfig.filters.projectFields) {
      mappedConfig.filters.projectFields = mappedConfig.filters.projectFields
        .map((field: any) => {
          if (field.source === 'checklist_item' && field.checklistItemId) {
            const mappedId = checklistMapping.mapped[Number(field.checklistItemId)];
            if (mappedId) {
              // Successfully mapped - update ID
              return { ...field, checklistItemId: mappedId, field: String(mappedId) };
            } else {
              // Failed to map - remove this filter
              return null;
            }
          }
          return field;
        })
        .filter((field: any) => field !== null);
    }

    // 4. Clear User and Team IDs (don't attempt mapping)
    delete mappedConfig.filters.assignedUserIds;
    delete mappedConfig.filters.teamIds;

    return mappedConfig;
  }

  /**
   * Generate user-friendly summary of what was mapped/removed (DASH-22.0)
   *
   * @param originalConfig - Original template config
   * @param mappedConfig - Mapped template config
   * @param sourceTenantName - Source tenant company name
   * @returns Summary with counts and user messages
   */
  private static generateMappingSummary(
    originalConfig: any,
    mappedConfig: any,
    sourceTenantName: string
  ): MappingSummary {
    const originalFilters = originalConfig.filters || {};
    const mappedFilters = mappedConfig.filters || {};

    // Count filters
    const originalCount =
      (originalFilters.pipelineIds?.length || 0) +
      (originalFilters.hiddenStageIds?.length || 0) +
      (originalFilters.assignedUserIds?.length || 0) +
      (originalFilters.teamIds?.length || 0) +
      (originalFilters.projectFields?.filter((f: any) => f.source === 'checklist_item')?.length || 0);

    const mappedCount =
      (mappedFilters.pipelineIds?.length || 0) +
      (mappedFilters.hiddenStageIds?.length || 0) +
      (mappedFilters.projectFields?.filter((f: any) => f.source === 'checklist_item')?.length || 0);

    const removedCount = originalCount - mappedCount;

    // Generate description
    let description = originalConfig.description || '';
    const shareNote = `Shared from ${sourceTenantName}`;

    if (removedCount > 0) {
      description += ` (${shareNote} - ${removedCount} filter(s) removed due to missing resources)`;
    } else {
      description += ` (${shareNote})`;
    }

    // Generate user message
    let userMessage = '';
    if (removedCount === 0) {
      userMessage = 'All filters mapped successfully';
    } else if (mappedCount === 0) {
      userMessage = 'All filters removed (no matching resources found)';
    } else {
      userMessage = `${mappedCount} of ${originalCount} filters mapped (${removedCount} removed)`;
    }

    return {
      description,
      originalCount,
      mappedCount,
      removedCount,
      userMessage,
    };
  }

  /**
   * Sets a dashboard as the default for the tenant (AC #4)
   * Clears any existing default and sets the new one
   */
  static async setDefaultDashboard(dashboardId: string): Promise<void> {
    try {
      const tenantId = await getCurrentTenantId();

      // Clear existing defaults for this tenant
      const { error: clearError } = await supabase
        .from('dashboards')
        .update({ is_default: false })
        .eq('tenant_id', tenantId)
        .eq('is_default', true);

      if (clearError) throw clearError;

      // Set new default
      const { error: setError } = await supabase
        .from('dashboards')
        .update({ is_default: true })
        .eq('id', dashboardId)
        .eq('tenant_id', tenantId);

      if (setError) throw setError;
    } catch (error) {
      console.error('Failed to set default dashboard:', error);
      throw new Error('Unable to set default dashboard. Please try again.');
    }
  }

  /**
   * Shares a dashboard (with all widgets) to a target tenant (DASH-20.1)
   * DASH-20.3: Includes automatic ID mapping for cross-tenant compatibility
   * Only accessible to superadmins with system.override permission.
   *
   * @param dashboardId - ID of the dashboard to share
   * @param targetTenantId - ID of the tenant to receive the dashboard
   * @returns Promise resolving to the new dashboard ID
   * @throws Error if operation fails (permission denied, not found, etc.)
   */
  static async shareDashboardWithTenant(
    dashboardId: string,
    targetTenantId: string
  ): Promise<string> {
    try {
      // Get source dashboard to determine source tenant
      const { data: sourceDashboard, error: fetchError } = await supabase
        .from('dashboards')
        .select('tenant_id')
        .eq('id', dashboardId)
        .single();

      if (fetchError || !sourceDashboard) {
        console.error('Failed to fetch source dashboard:', fetchError);
        throw new Error('Dashboard not found. Please check and try again.');
      }

      const sourceTenantId = sourceDashboard.tenant_id;

      // Call database function via RPC to duplicate dashboard
      const { data, error } = await supabase.rpc('duplicate_dashboard_to_tenant', {
        source_dashboard_id: dashboardId,
        target_tenant_id: targetTenantId,
      });

      if (error) {
        console.error('Failed to share dashboard:', error);

        // Transform technical errors into user-friendly messages
        if (error.message.includes('Permission denied')) {
          throw new Error('You do not have permission to share dashboards across tenants.');
        } else if (error.message.includes('not found')) {
          throw new Error('Dashboard or tenant not found. Please check and try again.');
        } else {
          throw new Error('Unable to share dashboard. Please try again.');
        }
      }

      const newDashboardId = data as string;

      // DASH-23.2: Log dashboard sharing (fire-and-forget)
      // Get target tenant name for logging
      const { data: targetTenant } = await supabase
        .from('tenants')
        .select('company_name')
        .eq('tenant_id', targetTenantId)
        .single();

      const targetTenantName = targetTenant?.company_name || 'Unknown Tenant';

      logDashboardActivity.shared(
        dashboardId,
        sourceTenantId,
        targetTenantId,
        targetTenantName,
        newDashboardId
      );

      // DASH-20.3: Apply ID mapping to widget configurations
      try {
        console.log('[DashboardService] Starting widget ID mapping...');
        const mappingResult = await WidgetMappingService.mapDashboardWidgets(
          newDashboardId,
          sourceTenantId,
          targetTenantId
        );

        console.log('[DashboardService] Mapping complete:', {
          widgetsProcessed: mappingResult.widgetsProcessed,
          pipelinesMapped: Object.keys(mappingResult.pipelines.mapped).length,
          pipelinesUnmapped: mappingResult.pipelines.unmapped.length,
          stagesMapped: Object.keys(mappingResult.stages.mapped).length,
          stagesUnmapped: mappingResult.stages.unmapped.length,
          checklistItemsMapped: Object.keys(mappingResult.checklistItems.mapped).length,
          checklistItemsUnmapped: mappingResult.checklistItems.unmapped.length,
          usersCleared: mappingResult.users.cleared.length,
          teamsCleared: mappingResult.teams.cleared.length,
        });

        // Store compatibility issues if any unmapped references exist
        const hasIssues =
          mappingResult.pipelines.unmapped.length > 0 ||
          mappingResult.stages.unmapped.length > 0 ||
          mappingResult.checklistItems.unmapped.length > 0 ||
          mappingResult.users.cleared.length > 0 ||
          mappingResult.teams.cleared.length > 0;

        if (hasIssues) {
          await this.storeCompatibilityIssues(
            newDashboardId,
            sourceTenantId,
            mappingResult
          );
          console.log('[DashboardService] Compatibility issues stored for user review');
        }
      } catch (mappingError) {
        // Log mapping error but don't block dashboard sharing
        console.error('[DashboardService] Widget mapping failed (non-blocking):', mappingError);
        console.warn('[DashboardService] Dashboard shared, but widgets may need manual configuration');
      }

      // Return new dashboard ID
      return newDashboardId;
    } catch (error) {
      // Re-throw if already an Error
      if (error instanceof Error) {
        throw error;
      }

      // Wrap unexpected errors
      console.error('Unexpected error sharing dashboard:', error);
      throw new Error('An unexpected error occurred. Please try again.');
    }
  }

  /**
   * Store compatibility issues for unmapped widget references
   * DASH-20.3: Tracks unmapped IDs for user remediation
   */
  private static async storeCompatibilityIssues(
    dashboardId: string,
    sourceTenantId: string,
    mappingResult: any
  ): Promise<void> {
    try {
      // Get source tenant name for display
      const { data: sourceTenant } = await supabase
        .from('tenants')
        .select('company_name')
        .eq('tenant_id', sourceTenantId)
        .single();

      const sourceTenantName = sourceTenant?.company_name || 'Unknown';

      // Get widgets to map issues to widget IDs
      const { data: widgets } = await supabase
        .from('dashboard_widgets')
        .select('id, config')
        .eq('dashboard_id', dashboardId);

      if (!widgets) return;

      const issues: Omit<CompatibilityIssue, 'id' | 'created_at'>[] = [];

      // For each widget with _needsConfiguration flag
      for (const widget of widgets) {
        const config = widget.config || {};
        if (!config._needsConfiguration) continue;

        const unmappedRefs = config._unmappedReferences || {};

        // Pipeline issues
        if (unmappedRefs.pipelines) {
          for (const ref of unmappedRefs.pipelines) {
            issues.push({
              widget_id: widget.id,
              dashboard_id: dashboardId,
              issue_type: 'pipeline',
              field_path: 'config.pipelineIds',
              unmapped_id: String(ref.id),
              original_name: ref.originalName,
              original_tenant_id: sourceTenantId,
              original_tenant_name: sourceTenantName,
              reason: 'no_matching_name',
              resolved_at: null,
            });
          }
        }

        // Stage issues
        if (unmappedRefs.stages) {
          for (const ref of unmappedRefs.stages) {
            issues.push({
              widget_id: widget.id,
              dashboard_id: dashboardId,
              issue_type: 'stage',
              field_path: 'config.hiddenStageIds',
              unmapped_id: String(ref.id),
              original_name: `${ref.originalName} (${ref.pipelineName})`,
              original_tenant_id: sourceTenantId,
              original_tenant_name: sourceTenantName,
              reason: 'parent_pipeline_not_mapped',
              resolved_at: null,
            });
          }
        }

        // Checklist item issues
        if (unmappedRefs.checklistItems) {
          for (const ref of unmappedRefs.checklistItems) {
            issues.push({
              widget_id: widget.id,
              dashboard_id: dashboardId,
              issue_type: 'checklist_item',
              field_path: 'config.projectFields',
              unmapped_id: String(ref.id),
              original_name: `${ref.originalName} (${ref.templateName})`,
              original_tenant_id: sourceTenantId,
              original_tenant_name: sourceTenantName,
              reason: 'item_not_found',
              resolved_at: null,
            });
          }
        }

        // User cleared issues
        if (unmappedRefs.users && unmappedRefs.users.length > 0) {
          issues.push({
            widget_id: widget.id,
            dashboard_id: dashboardId,
            issue_type: 'user',
            field_path: 'config.assignedUserIds',
            unmapped_id: 'all',
            original_name: 'User assignments cleared',
            original_tenant_id: sourceTenantId,
            original_tenant_name: sourceTenantName,
            reason: 'users_cleared_during_sharing',
            resolved_at: null,
          });
        }

        // Team cleared issues
        if (unmappedRefs.teams && unmappedRefs.teams.length > 0) {
          issues.push({
            widget_id: widget.id,
            dashboard_id: dashboardId,
            issue_type: 'team',
            field_path: 'config.teamIds',
            unmapped_id: 'all',
            original_name: 'Team assignments cleared',
            original_tenant_id: sourceTenantId,
            original_tenant_name: sourceTenantName,
            reason: 'teams_cleared_during_sharing',
            resolved_at: null,
          });
        }
      }

      // Batch insert compatibility issues
      if (issues.length > 0) {
        const { error } = await supabase
          .from('widget_compatibility_issues')
          .insert(issues);

        if (error) {
          console.error('[DashboardService] Failed to store compatibility issues:', error);
        } else {
          console.log(`[DashboardService] Stored ${issues.length} compatibility issues`);
        }
      }
    } catch (error) {
      console.error('[DashboardService] Error storing compatibility issues:', error);
      // Non-blocking - don't throw
    }
  }

  // ========== Dashboard Permissions Methods ==========

  /**
   * Creates dashboard permissions (batch insert)
   * Supports private, role-based, user-specific, and tenant-wide sharing
   */
  static async createDashboardPermissions(
    dashboardId: string,
    permissionType: 'private' | 'role' | 'user' | 'tenant',
    options: {
      roleIds?: number[];
      userIds?: string[];
      canEdit?: boolean;
    }
  ): Promise<void> {
    try {
      // Private type - no permissions to create
      if (permissionType === 'private') return;

      const permissions: any[] = [];

      if (permissionType === 'role' && options.roleIds) {
        // Create permission record for each role
        permissions.push(
          ...options.roleIds.map(roleId => ({
            dashboard_id: dashboardId,
            permission_type: 'role',
            role_id: roleId,
            user_id: null,
            can_view: true,
            can_edit: options.canEdit || false,
          }))
        );
      } else if (permissionType === 'user' && options.userIds) {
        // Create permission record for each user
        permissions.push(
          ...options.userIds.map(userId => ({
            dashboard_id: dashboardId,
            permission_type: 'user',
            role_id: null,
            user_id: userId,
            can_view: true,
            can_edit: options.canEdit || false,
          }))
        );
      } else if (permissionType === 'tenant') {
        // Create single tenant-wide permission
        permissions.push({
          dashboard_id: dashboardId,
          permission_type: 'tenant',
          role_id: null,
          user_id: null,
          can_view: true,
          can_edit: options.canEdit || false,
        });
      }

      // Batch insert all permissions
      if (permissions.length > 0) {
        const { error } = await supabase
          .from('dashboard_permissions')
          .insert(permissions);

        if (error) throw error;

        // DASH-23.2: Log permission creation (fire-and-forget)
        const tenantId = await getCurrentTenantId();
        if (tenantId) {
          logDashboardActivity.permissionsChanged(
            dashboardId,
            tenantId,
            permissionType,
            {
              affected_users: options.userIds,
              affected_roles: options.roleIds,
              can_edit: options.canEdit || false,
              can_view: true,
              action: 'created',
            }
          );
        }
      }
    } catch (error) {
      console.error('Failed to create dashboard permissions:', error);
      throw new Error('Unable to create dashboard permissions. Please try again.');
    }
  }

  /**
   * Gets all permissions for a dashboard
   */
  static async getDashboardPermissions(dashboardId: string): Promise<DashboardPermission[]> {
    try {
      const { data, error } = await supabase
        .from('dashboard_permissions')
        .select('*')
        .eq('dashboard_id', dashboardId);

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Failed to fetch dashboard permissions:', error);
      throw new Error('Unable to load dashboard permissions. Please try again.');
    }
  }

  /**
   * Deletes all permissions for a dashboard
   * Used in edit flow to clear old permissions before inserting new
   */
  static async deleteDashboardPermissions(dashboardId: string): Promise<void> {
    try {
      // DASH-23.2: Get existing permissions before deleting for logging
      const existingPermissions = await this.getDashboardPermissions(dashboardId);
      const hadPermissions = existingPermissions.length > 0;
      const previousPermissionType = existingPermissions[0]?.permission_type || 'private';

      const { error } = await supabase
        .from('dashboard_permissions')
        .delete()
        .eq('dashboard_id', dashboardId);

      if (error) throw error;

      // DASH-23.2: Log permission reset (fire-and-forget)
      if (hadPermissions) {
        const tenantId = await getCurrentTenantId();
        if (tenantId) {
          logDashboardActivity.permissionsChanged(
            dashboardId,
            tenantId,
            'private',
            {
              action: 'reset_to_private',
              previous_permission_type: previousPermissionType,
            }
          );
        }
      }
    } catch (error) {
      console.error('Failed to delete dashboard permissions:', error);
      throw new Error('Unable to delete dashboard permissions. Please try again.');
    }
  }

  /**
   * Checks if the current user can edit a dashboard
   * Returns true if user is creator, superadmin, tenant admin, or has permission with can_edit=true
   */
  static async canEditDashboard(dashboardId: string): Promise<boolean> {
    try {
      // Get current user
      const { data: userData } = await supabase.auth.getUser();
      const userId = userData?.user?.id;

      if (!userId) return false;

      // Get dashboard to check if user is creator and tenant
      const { data: dashboard, error: dashboardError } = await supabase
        .from('dashboards')
        .select('created_by, tenant_id')
        .eq('id', dashboardId)
        .single();

      if (dashboardError) throw dashboardError;

      // Creator can always edit
      if (dashboard?.created_by === userId) return true;

      // Get user's tenant and all roles using legacy-first pattern:
      // users.role_id is the primary role for normal users; user_roles holds
      // additional multi-tenant assignments (mostly Superadmin).
      const tenantId = await getCurrentTenantId();

      const userRolesMap = new Map<number, { id: number; name: string }>();

      const { data: userRow, error: userRowError } = await supabase
        .from('users')
        .select('role_id, roles!inner(id, name)')
        .eq('id', userId)
        .maybeSingle();

      if (userRowError) throw userRowError;
      if (userRow?.role_id && userRow.roles) {
        const role = Array.isArray(userRow.roles) ? userRow.roles[0] : (userRow.roles as any);
        if (role) userRolesMap.set(userRow.role_id, { id: role.id, name: role.name });
      }

      const { data: multiTenantRoles, error: roleError } = await supabase
        .from('user_roles')
        .select('role_id, roles(id, name)')
        .eq('user_id', userId)
        .eq('tenant_id', tenantId);

      if (roleError) throw roleError;
      multiTenantRoles?.forEach(ur => {
        const role = Array.isArray(ur.roles) ? ur.roles[0] : (ur.roles as any);
        if (ur.role_id != null && role) {
          userRolesMap.set(ur.role_id, { id: role.id, name: role.name });
        }
      });

      const userRoles = Array.from(userRolesMap.entries()).map(([role_id, roles]) => ({
        role_id,
        roles,
      }));

      // RBAC-7.4: ask the same two permissions the dashboards write policies ask, not role NAMES.
      // system.override edits every dashboard; settings.admin every dashboard in this account.
      const [platform, accountAdmin] = await Promise.all([
        supabase.rpc('holds_permission', { p_permission: DASHBOARD_PLATFORM_PERMISSION }),
        supabase.rpc('holds_permission', { p_permission: DASHBOARD_ACCOUNT_ADMIN_PERMISSION }),
      ]);
      if (
        adminMayEditDashboard({
          holdsPlatform: platform.data === true,
          holdsAccountAdmin: accountAdmin.data === true,
          dashboardTenantId: dashboard?.tenant_id,
          currentTenantId: tenantId,
        })
      ) {
        return true;
      }

      // Get all permissions for this dashboard
      const permissions = await this.getDashboardPermissions(dashboardId);

      // Check if any permission grants edit access
      for (const permission of permissions) {
        if (!permission.can_edit) continue; // Skip view-only permissions

        // Tenant-wide permission
        if (permission.permission_type === 'tenant') {
          return true;
        }

        // Role-based permission - check if user has any matching role
        if (permission.permission_type === 'role' && userRoles) {
          const hasMatchingRole = userRoles.some(ur => ur.role_id === permission.role_id);
          if (hasMatchingRole) {
            return true;
          }
        }

        // User-specific permission
        if (permission.permission_type === 'user' && permission.user_id === userId) {
          return true;
        }
      }

      return false; // Default to no edit access
    } catch (error) {
      console.error('Failed to check edit permission:', error);
      return false; // Fail closed - deny access on error
    }
  }
}
